import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PageMeta from "@/components/PageMeta";
import { CaretLeft, ShoppingCart, EnvelopeSimple } from "@phosphor-icons/react";

/* /shopping-list-help — one-stop "How to use the shopping list" page.
 * Pulls every FAQ that mentions the shopping list / pantry / Amazon (title
 * or body keyword match) so admins never have to hand-curate this list —
 * simply add new Shopping List FAQs in Admin → FAQs and they appear here
 * automatically. */

const KEYWORDS = ["shopping list", "shopping-list", "shopping", "pantry", "amazon", "supermarket", "aisle"];

function injectFaqSchema(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return undefined;
  const id = "rf-shopping-help-schema";
  let s = document.getElementById(id);
  if (!s) {
    s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    document.head.appendChild(s);
  }
  s.text = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  });
  return () => { const el = document.getElementById(id); if (el) el.remove(); };
}

export default function ShoppingListHelp() {
  const [faqs, setFaqs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/faqs")
      .then((r) => setFaqs(r.data || []))
      .catch(() => setFaqs([]))
      .finally(() => setLoading(false));
  }, []);

  // Keep only FAQs relevant to the Shopping List / Pantry / Amazon flows.
  const shoppingFaqs = useMemo(() => {
    const seen = new Set();
    return faqs.filter((f) => {
      if ((f.faq_id || "").startsWith("faq_import_guide_") || seen.has(f.question)) return false;
      seen.add(f.question);
      const hay = `${f.question || ""} ${f.answer || ""}`.toLowerCase();
      return KEYWORDS.some((kw) => hay.includes(kw));
    });
  }, [faqs]);

  useEffect(() => injectFaqSchema(shoppingFaqs), [shoppingFaqs]);

  const filtered = shoppingFaqs.filter(
    (f) => !q ||
      f.question.toLowerCase().includes(q.toLowerCase()) ||
      f.answer.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16" data-testid="shopping-help-page">
      <PageMeta
        title="How to use the Shopping List — Recipe Filer"
        description="Everything you can do with Recipe Filer's Shopping List: aisle grouping, cross-recipe quantity merging, pantry staples, Amazon UK 1-tap buy, and export to image, PDF, email or clipboard."
      />

      <Link to="/shopping" className="text-rf-orange font-bold text-sm inline-flex items-center gap-1 hover:underline mb-6" data-testid="shopping-help-back">
        <CaretLeft size={14} weight="bold" /> Back to your shopping list
      </Link>

      <div className="flex items-start gap-4 mb-8">
        <div className="w-14 h-14 rounded-2xl bg-rf-amber/25 flex items-center justify-center shrink-0">
          <ShoppingCart size={30} weight="duotone" className="text-rf-orange" />
        </div>
        <div>
          <p className="overline mb-2">Shopping List help</p>
          <h1 className="font-display text-4xl md:text-6xl font-black text-rf-ink leading-[0.95] tracking-tight">
            How to use the<br />
            <span className="text-rf-orange">Shopping List.</span>
          </h1>
          <p className="text-rf-ink-soft text-lg mt-4 max-w-2xl leading-relaxed">
            Every question we&apos;ve been asked about the aisle-grouped list, the pantry pin, Amazon buy links and the four export options — answered on one page.
          </p>
        </div>
      </div>

      <input
        data-testid="shopping-help-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search shopping-list FAQs…"
        className="input-rf mb-8"
      />

      {loading && (
        <p className="text-rf-ink-soft text-center py-10">Loading answers…</p>
      )}

      {!loading && filtered.length === 0 && (
        <p className="text-rf-ink-soft text-center py-10" data-testid="shopping-help-empty">
          {q ? "Nothing matches that search — try a different keyword." : "No shopping-list FAQs yet."}
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <Accordion type="multiple" className="space-y-2" data-testid="shopping-help-accordion">
          {filtered.map((f) => (
            <AccordionItem key={f.faq_id} value={f.faq_id} className="border border-rf-ink/10 rounded-2xl bg-white px-5">
              <AccordionTrigger
                data-testid={`shopping-help-faq-${f.faq_id}`}
                className="text-left font-display font-bold text-base md:text-lg text-rf-ink hover:no-underline"
              >
                {f.question}
              </AccordionTrigger>
              <AccordionContent className="text-rf-ink-soft leading-relaxed whitespace-pre-line">
                {f.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      <div className="mt-12 card-recipe text-center" data-testid="shopping-help-contact">
        <p className="font-display font-extrabold text-rf-ink text-lg mb-1">Still stuck?</p>
        <p className="text-rf-ink-soft text-sm inline-flex items-center gap-1.5 justify-center">
          <EnvelopeSimple size={16} weight="duotone" className="text-rf-orange" />
          Email us at{" "}
          <a href="mailto:contactus@recipefiler.com" className="text-rf-orange font-bold underline">
            contactus@recipefiler.com
          </a>
        </p>
      </div>
    </div>
  );
}
